import AspectRatioIcon from "@mui/icons-material/AspectRatio";
import cx from "classnames";
import React, { useContext } from "react";
import { toast } from "react-toastify";

import { ReactComponent as DeleteIcon } from "assets/delete-icon.svg";
import { ReactComponent as EditIcon } from "assets/pen.svg";
import ShotContext from "components/Shot/ShotContext";
import { STATUSES, WINDOW_EVENTS } from "constants/constants";
import { Cutout } from "models/Cutout";
import { LayerCompact } from "models/Layer";
import { deleteCutout } from "services/CutoutService";

import styles from "./Layers.module.scss";

interface CutoutCardProps {
  cutout: Cutout;
  layer: LayerCompact;
  /** Switch the active layer to this cutout's layer (e.g. navigate). */
  onActivateLayer?: () => void;
}

const CutoutCard: React.FC<CutoutCardProps> = ({
  cutout,
  layer,
  onActivateLayer,
}) => {
  const { layerId, selectedCutout, setSelectedCutout, setShotCutouts } =
    useContext(ShotContext);

  const isLayerActive = layerId !== undefined && +layerId === layer.id;
  const isSelected = selectedCutout?.id === cutout.id;

  const thumbUrl = cutout.cover_image_url || cutout.image_url;

  const inferenceStatus = cutout.inference?.status;
  const isInferencing = inferenceStatus === STATUSES.IN_PROGRESS;
  const isInferenceFailed = inferenceStatus === STATUSES.FAILED;
  const isInferenceComplete = inferenceStatus === STATUSES.COMPLETED;

  // Mirrors the backend gate (`CutoutDetailAPIView.delete` +
  // `CutoutEditSerializer`): no edit/delete once an `InferenceCutout` exists.
  const canMutate = cutout.inference?.status == null;

  const handleSelect = () => {
    if (!isLayerActive) onActivateLayer?.();
    setSelectedCutout((prev) => (prev?.id === cutout.id ? null : cutout));
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isLayerActive) onActivateLayer?.();
    window.dispatchEvent(
      new CustomEvent(WINDOW_EVENTS.ANNOTATE, {
        detail: { frame: cutout.frame },
      }),
    );
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(
      `Delete cutout on frame ${cutout.frame}? This can't be undone.`,
    );
    if (!confirmed) return;
    try {
      await deleteCutout(cutout.id);
      setShotCutouts((prev) => prev.filter((c) => c.id !== cutout.id));
      if (selectedCutout?.id === cutout.id) setSelectedCutout(null);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to delete cutout";
      toast.error(message);
    }
  };

  return (
    <div
      className={cx(
        styles.cutoutCard,
        isSelected && styles.cutoutCardSelected,
        isInferencing && styles.cutoutCardInferencing,
        isInferencing && isSelected && styles.cutoutCardInferencingSelected,
        isInferenceFailed && styles.cutoutCardInferenceFailed,
        isInferenceComplete && styles.cutoutCardInferenceSuccess,
      )}
      onClick={(e) => {
        e.stopPropagation();
        handleSelect();
      }}
      role="button"
      tabIndex={0}
      aria-pressed={isSelected}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleSelect();
        }
      }}
    >
      <div
        className={styles.cutoutThumbnail}
        style={{ backgroundColor: layer.color }}
      >
        {isInferencing && (
          <div className={styles.statusOverlay}>Inferencing...</div>
        )}
        {isInferenceFailed && (
          <div className={styles.statusOverlay}>
            Error occurred while inferencing
          </div>
        )}
        {thumbUrl ? (
          <img alt={`Frame ${cutout.frame}`} src={thumbUrl} />
        ) : (
          <AspectRatioIcon className={styles.cutoutFallbackIcon} />
        )}
      </div>
      <div className={styles.cutoutFooter}>
        <span className={styles.cutoutName}>Fr: {cutout.frame}</span>
        {canMutate && (
          <div className={styles.cutoutActions}>
            <button
              type="button"
              className={styles.cutoutActionButton}
              onClick={handleEdit}
              aria-label="Edit cutout"
            >
              <EditIcon />
            </button>
            <button
              type="button"
              className={styles.cutoutActionButton}
              onClick={handleDelete}
              aria-label="Delete cutout"
            >
              <DeleteIcon />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CutoutCard;
